import {
	MediaQueryKind,
	useWorkspaceMediaQuery,
} from "client/Hooks";
import {
	ReactElement,
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	GlobalSearchCategoryResult,
	GlobalSearchCategoryResultStatus,
	IGlobalSearchResultItem,
	SearchIntegrationKind,
	GlobalSearchScopeKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { ItemView } from "./ItemView/ItemView";
import { ErrorMessage } from "../../ErrorMessage/ErrorMessage";
import { localize } from "client_legacy/ndaw/culture";
import {
	generateTrackingId,
	isNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	NoResults
} from "client/Components";
import { ScopeActions } from "../../GlobalSearch";
import { TimeZone } from "client/DateAndTime/timezones";
import { never } from "client_legacy/ndaw/errors";
import { ResultSectionLayout } from "../../ResultSectionLayout/ResultSectionLayout";
import { ContactSource } from "client/Api/Email/contacts";

export type CategoryViewProps = {
	result: GlobalSearchCategoryResult;
	navigationStartIndex: number;
	searchTerm: string;
	availableActions: ScopeActions;
	onReload: () => void;
	timezone: TimeZone;
	onOpenContactDetails: (id: string, source: ContactSource) => void;
	onDismiss: () => void;
	tabbableIndex: number | null;
};

export const CategoryView = (props: CategoryViewProps): ReactElement => {
	const {
		result,
		navigationStartIndex,
		availableActions,
		searchTerm,
		onReload,
		timezone,
		onOpenContactDetails,
		onDismiss,
		tabbableIndex
	} = props;

	const {
		itemsScope,
		name,
		status,
		items
	} = result;

	const [wasAuthenticateInvoked, setWasAuthenticateInvoked] = useState<boolean>(false);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const onAuthenticate = useMemo(
		(): (() => void) | undefined => {

			switch(itemsScope.kind) {
				case GlobalSearchScopeKind.Predefined:
					return availableActions.onAuthenticate;
				case GlobalSearchScopeKind.IntegrationFlow:
					return () => {
						availableActions.onIntegrationFlowAuthenticate?.(
							itemsScope.integrationId,
							name
						);
					};
				default:
					never(itemsScope);
			}
		},
		[
			availableActions,
			itemsScope,
			name
		]
	);

	const authenticateConfiguration = useMemo(
		(): { onRetry: undefined; } | { onRetry: () => void; retryText: string; } => {
			if (isNil(onAuthenticate)) {
				return {
					onRetry: undefined
				};
			}

			if (wasAuthenticateInvoked) {
				return {
					onRetry: onReload,
					retryText: localize("_Finish")
				};
			}

			return {
				onRetry: (): void => {
					setWasAuthenticateInvoked(true);
					onAuthenticate();
				},
				retryText: localize("_Authenticate")
			};
		},
		[
			onAuthenticate,
			onReload,
			wasAuthenticateInvoked
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(result);
			setWasAuthenticateInvoked(false);
		},
		[result]
	);

	const scope: SearchIntegrationKind | string = itemsScope.kind === GlobalSearchScopeKind.Predefined
		? itemsScope.predefinedKind
		: `integration-id-${itemsScope.integrationId}`;

	return (
		<ResultSectionLayout
			trackingId={generateTrackingId("category", `|${scope}`)}
			title={name}
		>
			{status === GlobalSearchCategoryResultStatus.AuthenticationRequired && (
				<NoResults
					{...authenticateConfiguration}
					trackingId="authentication-required"
					icon="gear"
					title={localize("_AuthenticationRequired")}
					message={{
						template: localize("_ToUseNameAuthenticationIsNeeded"),
						replacements: [
							{ what: "{{Name}}", filler: result.name }
						]
					}}
				/>
			)}

			{status === GlobalSearchCategoryResultStatus.Failed && (
				<ErrorMessage message={localize("_GlobalSearchError")} />
			)}

			{status === GlobalSearchCategoryResultStatus.Success && (
				<ResultSectionLayout.List>
					{items.map((item: IGlobalSearchResultItem, index: number): ReactElement => {
						const itemIndex: number = navigationStartIndex + index;

						return (
							<ItemView
								item={item}
								key={itemIndex}
								searchTerm={searchTerm}
								itemScope={itemsScope}
								isMobile={isMobile}
								timezone={timezone}
								onOpenContactDetails={onOpenContactDetails}
								onDismiss={onDismiss}
								itemIndex={itemIndex}
								tabIndex={tabbableIndex === itemIndex ? 0 : -1}
							/>
						);
					})}
				</ResultSectionLayout.List>
			)}
		</ResultSectionLayout>
	);
};

CategoryView.displayName = "CategoryView";
